import { Text } from '@/components/ui/text'
import { CommanderScore } from '@components/SetupCommanderScore'
import { CommunityScore } from '@components/SetupCommunityScore'
import SetupDuration from '@components/SetupDuration'
import SetupPotential from '@components/SetupPotential'
import SetupRRR from '@components/SetupRRR'
import type { Tables } from '@hvf/contracts'
import { Star } from 'lucide-solid'
import { Show } from 'solid-js'
import { useService } from 'solid-services'
import { Box, Flex, Grid } from 'styled-system/jsx'
import SpecificationBadge from '../../../SpecificationBadge'
import { stageColor } from '../../../StageBadge'
import { useSingle } from '../../state'

export default function SpecsPanel(props: { setup: Tables<'setup'> }) {
  const { takenAverageScoreQuery } = useService(useSingle)()

  const score = () => {
    const val = takenAverageScoreQuery.data
    if (val == null) return null
    return Number.isInteger(val) ? val : val.toFixed(1)
  }

  const entry = () =>
    props.setup.position === 'long' ? props.setup.high_3_price : props.setup.low_3_price
  const stop = () =>
    props.setup.position === 'long' ? props.setup.low_3_price : props.setup.high_3_price

  const statusLabel = () => (props.setup.stage || 'unspecified').replaceAll('_', ' ').replaceAll('-', ' ')
  const c = () => stageColor(props.setup.stage)

  const Cell = (cellProps: { title: string; children: any }) => (
    <Box bg="#ffffff" px="8px" py="11px" textAlign="center" minWidth={0}>
      <Text
        fontSize="9px"
        letterSpacing=".12em"
        textTransform="uppercase"
        color="#8b909b"
        fontWeight="600"
        mb="5px"
      >
        {cellProps.title}
      </Text>
      {cellProps.children}
    </Box>
  )

  return (
    <Box
      mt="13px"
      border="1px solid rgba(20, 22, 30, 0.09)"
      borderRadius="14px"
      overflow="hidden"
      bg="#ffffff"
    >
      <Flex alignItems="center" justifyContent="space-between" gap="12px" px="14px" py="12px">
        <Flex
          alignItems="center"
          gap="7px"
          fontSize="13px"
          fontWeight="700"
          color="#14161b"
          borderRadius="8px"
          px="11px"
          py="6px"
          style={{
            background: `color-mix(in srgb, ${c()} 13%, #ffffff)`,
            border: `1.5px solid color-mix(in srgb, ${c()} 48%, transparent)`,
          }}
        >
          <Box w="7px" h="7px" borderRadius="full" style={{ background: c() }} />
          <Text textTransform="capitalize" fontSize="13px" fontWeight="700" color="#14161b">
            {statusLabel()}
          </Text>
        </Flex>
        <Flex alignItems="baseline" gap="6px">
          <Text fontSize="10px" letterSpacing=".08em" textTransform="uppercase" color="#8b909b">RRR</Text>
          <SetupRRR setup={props.setup} fontSize="22px" />
        </Flex>
      </Flex>

      <Grid
        gridTemplateColumns="1fr 1fr 1fr"
        gap="1px"
        style={{ background: 'rgba(20, 22, 30, 0.09)' }}
        borderTop="1px solid rgba(20, 22, 30, 0.09)"
      >
        <Cell title="Entry">
          <Text fontSize="14.5px" fontWeight="700" color="#14161b">{entry()}</Text>
        </Cell>
        <Cell title="Target">
          <Text fontSize="14.5px" fontWeight="700" color="#0a8f63">{props.setup.target}</Text>
        </Cell>
        <Cell title="Stop">
          <Text fontSize="14.5px" fontWeight="700" color="#e5604f">{stop()}</Text>
        </Cell>
        <Cell title="Pattern spec">
          <SpecificationBadge patternSpecification={props.setup.pattern_specification} />
        </Cell>
        <Cell title="Timeframe">
          <SetupDuration setup={props.setup} fontSize="14.5px" />
        </Cell>
        <Cell title="Potential">
          <Flex justifyContent="center">
            <SetupPotential setup={props.setup} size="26px" />
          </Flex>
        </Cell>
        <Cell title="PHQ">
          <CommanderScore setupId={props.setup.id} />
        </Cell>
        <Cell title="Community">
          <CommunityScore setup={props.setup} />
        </Cell>
        <Cell title="Your score">
          <Show
            when={score()}
            fallback={<Text fontSize="14px" fontWeight="700" color="#8b909b">-</Text>}
          >
            <Flex alignItems="center" justifyContent="center" gap="5px">
              <Star size={14} fill="#e0a72a" stroke="#e0a72a" />
              <Text fontSize="14px" fontWeight="700" color="#14161b">{score()}</Text>
            </Flex>
          </Show>
        </Cell>
      </Grid>
    </Box>
  )
}
